import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ParseKeys } from "i18next";
import { Box } from "@mui/material";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";

import Tabs, { TabsProps } from "components/basic/tabs";
import Button from "components/basic/button";
import LoadingSpinner from "components/basic/loadingSpinner";
import Scrollbar from "components/basic/scrollbar";
import { useSnack } from "hooks/useSnack";
import useUrlParamState from "hooks/useUrlParamState";

import ChatWindow from "./components/ChatWindow";
import { useChatPopups } from "./components/ChatPopups";
import ConversationList from "./components/ConversationList";
import NewConversationDialog from "./components/NewConversationDialog";
import { MESSAGE_TABS } from "./constants";
import { filterByTab, sortByActivity } from "./helpers";
import { useConversations, useMessageActions } from "./messagesApi";
import { ConversationView, TabKey } from "./types";

const EMPTY_TEXT: Record<TabKey, ParseKeys<"messages">> = {
  all: "empty.all",
  starred: "empty.starred",
  unread: "empty.unread",
  archived: "empty.archived",
};

const Messaging = () => {
  const showSnack = useSnack();
  const { t } = useTranslation("messages");
  const { popOut } = useChatPopups();

  const [selectedTab, setSelectedTab] = useState<TabKey>("all");
  const [selectedParam, setSelectedParam] = useUrlParamState("c");
  const [dialogOpen, setDialogOpen] = useState(false);

  const { conversations, loading, refresh } = useConversations();
  const { setStarred, setArchived } = useMessageActions();

  const selectedId = selectedParam ? Number(selectedParam) : null;
  const selected = conversations.find((c) => c.id === selectedId) ?? null;
  const visible = sortByActivity(filterByTab(conversations, selectedTab));

  const tabData: TabsProps = {
    selectedTab,
    setSelectedTab: (tab: string) => setSelectedTab(tab as TabKey),
    tabs: MESSAGE_TABS.map((tab) => ({ ...tab, label: t(tab.label) })),
  };

  const handleToggleStar = async (conversation: ConversationView) => {
    const result = await setStarred(conversation.id, !conversation.starred);
    if (!result.ok) {
      showSnack(result.error ?? t("snack.update_failed"), "error");
    }
  };

  const handleToggleArchive = async (conversation: ConversationView) => {
    const result = await setArchived(conversation.id, !conversation.archived);
    if (!result.ok) {
      showSnack(result.error ?? t("snack.update_failed"), "error");
    }
  };

  const openConversation = (conversationId: number) =>
    setSelectedParam(String(conversationId));

  // Full-screen thread view; the list is a separate full-screen state below.
  if (selected) {
    return (
      <Box display="flex" flexDirection="column" height="100%">
        <ChatWindow
          key={selected.id}
          conversation={selected}
          onBack={() => setSelectedParam(null)}
          onPopOut={() => {
            // Hand the chat over to a floating popup and drop back to the
            // list so the conversation isn't open in two places at once.
            popOut(selected.id);
            setSelectedParam(null);
          }}
          onToggleStar={() => handleToggleStar(selected)}
          onToggleArchive={() => handleToggleArchive(selected)}
        />
      </Box>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Tabs {...tabData} />

      <Box display="flex" justifyContent="flex-end" px={2} py={1}>
        <Button
          size="small"
          startAdornment={<AddCommentOutlinedIcon fontSize="small" />}
          onClick={() => setDialogOpen(true)}
        >
          {t("new_message")}
        </Button>
      </Box>

      <Scrollbar sx={{ flex: 1, minHeight: 0 }} showHint={false}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <LoadingSpinner />
          </Box>
        ) : (
          <ConversationList
            conversations={visible}
            selectedId={selectedId}
            emptyText={t(EMPTY_TEXT[selectedTab])}
            onSelect={(c) => openConversation(c.id)}
            onToggleStar={handleToggleStar}
            onToggleArchive={handleToggleArchive}
          />
        )}
      </Scrollbar>

      {dialogOpen && (
        <NewConversationDialog
          onClose={() => setDialogOpen(false)}
          onOpenConversation={(conversationId) => {
            setDialogOpen(false);
            // A just-started conversation isn't in the polled list yet.
            refresh();
            openConversation(conversationId);
          }}
        />
      )}
    </div>
  );
};

export default Messaging;
